import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { SubscriberFilter, SubscriberListItemDto } from '@tiffinify/contracts';
import { formatShort } from '@tiffinify/core';
import { BottomNav, Icon, LoadError, PageLoader } from '../components/ui';
import { Av, Badge, Card, Empty, Ends, IB, Kick, Note, Row, Screen, Seg, Sp, Top } from '../components/p';
import { CreateButton } from '../components/nav';
import { FlashBanner } from '../lib/flash';
import { formatInr, mealsLabel } from '../lib/format';
import { useRegistrationInbox, useSubscribers } from '../lib/queries';

const FILTERS: Array<{ id: SubscriberFilter; label: string }> = [
  { id: 'active', label: 'Active' },
  { id: 'ending', label: 'Ending' },
  { id: 'paused', label: 'Paused' },
  { id: 'payment', label: 'Due' },
  { id: 'past', label: 'Past' },
];

const EMPTY: Record<SubscriberFilter, [string, string]> = {
  active: ['No active subscribers', 'Add a subscription to start delivering tiffins.'],
  ending: ['No subscriptions ending soon', 'All clear.'],
  paused: ['No paused subscribers', 'All clear.'],
  payment: ['No payments due', 'All clear.'],
  past: ['No past customers', 'Closed plans and subscriptions from earlier months will appear here.'],
  all: ['No customers yet', 'Add a subscription to start.'],
};

/** One person, as drawn (P21): who, the plan and when it ends, one badge. */
function SubscriberRow({ i }: { i: SubscriberListItemDto }) {
  const ending = i.tags.includes('ending');
  const paused = i.tags.includes('paused');
  const line = [i.slot ? mealsLabel(i.slot) : null,
    i.state === 'closed' ? 'closed' : i.state === 'ended' ? 'ended' : paused ? 'paused' : i.endDate ? `ends ${formatShort(i.endDate)}` : null,
    i.area].filter(Boolean).join(' · ');
  const right = i.due > 0 ? <Badge tone="s">{formatInr(i.due)} due</Badge>
    : ending && i.endDate ? <Badge tone="r">Ends {formatShort(i.endDate)}</Badge>
    : paused ? <Badge>Paused</Badge>
    : i.state === 'closed' ? <Badge>Closed</Badge>
    : i.state === 'ended' ? <Badge>Ended</Badge>
    : i.state === 'active' && i.left !== null ? <Ends amt={`${i.left} left`} badge={<Badge tone="g">Paid</Badge>} />
    : undefined;
  return <Row to={`/s/${i.subscriberId}`} left={<Av name={i.name} />} t={i.name} s={line} right={right} />;
}

export function Subscribers() {
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  // Search once typing pauses, not on every key.
  const [asked, setAsked] = useState('');
  useEffect(() => { const t = setTimeout(() => setAsked(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  const [filter, setFilter] = useState<SubscriberFilter>('active');
  const list = useSubscribers(asked, filter);
  const d = list.data;
  const inbox = useRegistrationInbox();
  const waiting = inbox.data?.requests.length ?? 0;
  const [title, body] = asked ? ['No one matches', 'Try a different name, phone or area.'] : EMPTY[filter];

  return (
    <>
      <Screen gap={18}>
        <Top>
          <div className="p-stack"><Kick>{d ? `${d.counts.active} on a plan` : 'Subscribers'}</Kick><h1 className="p-h1">Subscribers</h1></div>
          <Sp />
          <IB icon={searching ? 'close' : 'search'} label={searching ? 'Close search' : 'Search subscribers'} onClick={() => { setSearching(!searching); setQ(''); }} />
        </Top>
        <FlashBanner />
        {searching && (
          <label className="p-search">
            <Icon name="search" size={18} sw={2} />
            <input aria-label="Search subscribers" autoFocus value={q} placeholder="Name, phone or area" onChange={(e) => setQ(e.target.value)} />
          </label>
        )}
        {list.isPending || inbox.isPending ? (
          <PageLoader label="Finding subscribers" />
        ) : (
          <>
            {waiting > 0 && (
              <Note icon="people" to="/registrations">
                <b>{waiting} {waiting === 1 ? 'sign-up' : 'sign-ups'} waiting</b> from your link · <span style={{ textDecoration: 'underline' }}>Review</span>
              </Note>
            )}
            <Seg label="Filter subscribers" value={filter} onChange={setFilter}
              options={FILTERS.map((f) => ({ value: f.id, label: f.id === 'active' || !d ? f.label : `${f.label} ${d.counts[f.id]}` }))} />
            {list.isError && !d ? (
              <LoadError what="your customers" onRetry={() => void list.refetch()} />
            ) : !d || d.items.length === 0 ? (
              <Card><Empty title={title}>{body}</Empty></Card>
            ) : (
              <Card>{d.items.map((i) => <SubscriberRow key={i.subscriberId} i={i} />)}</Card>
            )}
          </>
        )}
        {!searching && d && <Link className="p-back" to="/registrations" style={{ alignSelf: 'center' }}>Your sign-up link</Link>}
      </Screen>
      <CreateButton to="/new" label="New subscription" />
      <BottomNav />
    </>
  );
}
